// CapturePage.tsx — `/scan`: the phone scanner. A full-bleed rear-camera
// video with a canvas overlay (marker outlines, world axes, SLAM map points),
// a tracking-state badge, the IMU-fused chip, the lens-profile chip (§14:
// "Lens profile: …" or "Calibrating new device", plus a mismatch warning), the viewpoint coverage ring, a
// stats strip and a live splat preview. Start (which also asks for motion
// permission on iOS) → sweep → Finish; after Finish the page follows server
// refinement over /live and the preview swaps to the server's splats. While
// tracking is paused (phone rotated away from the session's orientation, or
// the keyframe upload backlog is full) the hint says why; a Finish whose
// server call failed offers Retry finish.
// Adaptive offload (§15): a "Process on server" switch (or ?mode=server)
// hands tracking to the server from the start, and a phone too slow to track
// live switches on its own; then the page shows the reason, a "Server
// processing" chip, and drives the tracking badge, stats strip and preview
// from the server's tracking / server_tracking / SNAP+DELT messages. After
// Finish in server mode the scan stays `capturing` while the server drains
// the video (phase 'processing'): the page keeps showing that progress until
// refinement starts.
// Video archive (§15.6): in device mode the recording uploads beside the
// keyframes and keeps going after Finish (also across navigation to the
// viewer); the stats strip shows "uploading video n/m".
// Live mode (§15.7): a "Live splats" switch (remembered per browser, on by
// default) asks a server-mode upload to train splats on the server's GPU as
// the video streams in. Then the preview is the live model, the stats strip
// gains a "live · 12 kf · 41k gauss · 3 Hz · 2 overruns" line, the
// server-tracked camera path is drawn over the preview, and a muted note says
// when live splats are unavailable (the server's reason) or the live model
// was handed to refinement. Device-mode captures never ask for it.
// Flash lighting (§16.1): an opt-in "Flash lighting" switch (or ?flash=1)
// toggles the torch during capture and locks exposure at the first marker.
import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { BsArrowClockwise, BsCameraVideo, BsCheck2, BsCloudArrowUp, BsEye } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { StatusBadge, ui } from 'common';
import { api, computeLoginUrl, hasCompute } from '../api';
import type { MapPointJSON, Me, Scan } from '../api';
import { drawOverlay } from '../capture/overlay';
import { parseFlashParam } from '../capture/flash';
import { parseModeOverride, parseSimulateSlowMs } from '../capture/perf';
import { loadLiveSplats, saveLiveSplats } from '../capture/prefs';
import type { OffloadDecision } from '../capture/perf';
import { chipText } from '../capture/profile';
import type { DeviceInfo } from '../capture/profile';
import { CaptureSession } from '../capture/session';
import type { CaptureStats, SessionPhase } from '../capture/session';
import { liveStreaming } from '../capture/serverfeed';
import type { LiveStats, ServerTrackingStats, TrailSnapshot } from '../capture/serverfeed';
import type { FrameResultJSON, PoseSource, TrackingState } from '../capture/worker';
import { CoverageRing } from '../components/CoverageRing';
import { RefineProgress } from '../components/RefineProgress';
import { SplatView } from '../components/SplatView';
import { fmtCount } from '../format';
import type { ProgressMessage } from '../live';
import type { SplatCloud } from '../splat/format';

const { Text } = ui;

function trackingBadge(state: TrackingState | null, source: PoseSource): { tone: 'success' | 'warning' | 'danger' | 'neutral'; text: string } {
  switch (state) {
    case 'tracking':
      return { tone: 'success', text: `tracking · ${source || 'features'}` };
    case 'lost':
      return { tone: 'danger', text: 'lost' };
    case 'initializing':
      return { tone: 'warning', text: 'initializing' };
    default:
      return { tone: 'neutral', text: 'idle' };
  }
}

const TRACKING_STATES: readonly string[] = ['initializing', 'tracking', 'lost'];

// asTrackingState narrows the server's tracking state string.
function asTrackingState(s: string): TrackingState | null {
  return TRACKING_STATES.includes(s) ? (s as TrackingState) : null;
}

// statsLine is the stats strip: phone tracker numbers in device mode, the
// server tracker's progress and the upload in server mode.
function statsLine(s: CaptureStats): string[] {
  if (s.processing === 'server') {
    return [
      `markers ${s.markers}`,
      `keyframes ${s.keyframes}`,
      `quality ${(s.quality * 100).toFixed(0)}%`,
      `server frames ${s.serverFrames} · lag ${s.serverLagS.toFixed(1)} s`,
      `video chunks ${s.chunksUploaded}/${s.chunksRecorded} uploaded (${(s.uploadedBytes / 1048576).toFixed(1)} MB)`,
    ];
  }
  return [
    `markers ${s.markers}`,
    `reproj ${s.reprojPx.toFixed(2)} px`,
    `keyframes ${s.keyframes}${s.pendingUploads ? ` (${s.pendingUploads} uploading)` : ''}`,
    `quality ${(s.quality * 100).toFixed(0)}%`,
    `${s.fps.toFixed(1)} fps · ${s.trackMs} ms`,
    `coverage ${(s.coverage * 100).toFixed(0)}%`,
    ...(s.videoArchive ? [archiveLine(s)] : []),
  ];
}

// liveLine is the live-mode stats (§15.7): keyframes trained on, model
// size, snapshot rate and GPU budget overruns.
function liveLine(l: LiveStats): string {
  if (l.phase === 'waiting') return 'live · waiting for frames';
  const parts = ['live', `${l.keyframes} kf`, `${fmtCount(l.gaussians)} gauss`];
  if (l.hz > 0) parts.push(`${Number(l.hz.toFixed(1))} Hz`);
  if (l.psnr > 0) parts.push(`${l.psnr.toFixed(1)} dB`);
  if (l.overruns > 0) parts.push(`${l.overruns} overrun${l.overruns === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// liveNote is the muted line under the stage: why live splats are off, or
// the hand-off of the live model to refinement.
function liveNote(l: LiveStats | null): string {
  if (!l) return '';
  if (l.phase === 'off') return `Live splats unavailable${l.message ? `: ${l.message}` : ''}`;
  if (l.phase === 'handoff' || l.phase === 'done') return l.message || 'Refining from the live model';
  return '';
}

// archiveLine is the device-mode video archive's progress (§15.6): chunks
// go up behind the keyframes, the rest after Finish.
function archiveLine(s: CaptureStats): string {
  const mb = (s.uploadedBytes / 1048576).toFixed(1);
  switch (s.videoArchive) {
    case 'complete':
      return `video saved (${mb} MB)`;
    case 'partial':
      return `video saved with gaps (${mb} MB)`;
    case 'failed':
      return 'video upload failed';
    case 'closed':
      return 'video upload stopped';
    default:
      return `uploading video ${s.chunksUploaded}/${s.chunksRecorded} (${mb} MB)`;
  }
}

export default function CapturePage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<CaptureSession | null>(null);
  const [phase, setPhase] = useState<SessionPhase>('idle');
  const [scan, setScan] = useState<Scan | null>(null);
  const [name, setName] = useState('');
  const [tracking, setTracking] = useState<TrackingState | null>(null);
  const [source, setSource] = useState<PoseSource>('');
  const [stats, setStats] = useState<CaptureStats | null>(null);
  const [preview, setPreview] = useState<SplatCloud | null>(null);
  const [previewSource, setPreviewSource] = useState<'phone' | 'server'>('phone');
  const [progress, setProgress] = useState<ProgressMessage | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [forceServer, setForceServer] = useState(() => parseModeOverride(window.location.search) === 'server');
  const [lighting, setLighting] = useState(() => parseFlashParam(window.location.search));
  const [offload, setOffload] = useState<OffloadDecision | null>(null);
  const [serverTracking, setServerTracking] = useState<ServerTrackingStats | null>(null);
  const [liveSplats, setLiveSplats] = useState(() => loadLiveSplats());
  const [live, setLive] = useState<LiveStats | null>(null);
  const [trail, setTrail] = useState<TrailSnapshot | null>(null);
  // Who is signed in: without openscan.compute the server refuses to process
  // the capture (video/start, finish), so the page offers the compute sign-in
  // up front, and again when the server refuses.
  const [me, setMe] = useState<Me | null>(null);
  const [computeRefused, setComputeRefused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((m) => {
        if (cancelled) return;
        setMe(m);
        if (!hasCompute(m)) console.info('[openscan/capture] signed in without openscan.compute: server processing will be refused');
      })
      .catch((e) => console.info('[openscan/capture] /api/me unavailable:', e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  const onFrame = useCallback((res: FrameResultJSON, fw: number, fh: number, mapPoints: readonly MapPointJSON[]) => {
    setTracking(res.state);
    setSource(res.pose_source);
    const canvas = overlayRef.current, stage = stageRef.current;
    if (!canvas || !stage) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = stage.clientWidth, h = stage.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext('2d');
    if (ctx) drawOverlay(ctx, w, h, fw, fh, res, mapPoints, dpr);
  }, []);

  // onOffload: the phone stopped tracking; clear its last overlay drawing.
  const onOffload = useCallback((d: OffloadDecision) => {
    setOffload(d);
    const canvas = overlayRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getSession = useCallback((server: boolean, flash: boolean, liveOn: boolean): CaptureSession => {
    if (!sessionRef.current) {
      const search = window.location.search;
      const opts = {
        override: parseModeOverride(search), forceServer: server, simulateSlowMs: parseSimulateSlowMs(search),
        lighting: flash || parseFlashParam(search), liveSplats: liveOn,
      };
      console.info('[openscan/capture] new session', opts);
      sessionRef.current = new CaptureSession({
        onPhase: (p, s) => {
          setPhase(p);
          setScan(s);
        },
        onFrame,
        onStats: setStats,
        onPreview: (cloud, src) => {
          setPreview(cloud);
          setPreviewSource(src);
        },
        onProgress: setProgress,
        onError: (m) => setErrors((prev) => [...prev.slice(-3), m]),
        onDevice: setDevice,
        onOffload,
        onTracking: setServerTracking,
        onLive: setLive,
        onTrail: setTrail,
        onComputeRefused: () => setComputeRefused(true),
      }, opts);
    }
    return sessionRef.current;
  }, [onFrame, onOffload]);

  useEffect(() => () => sessionRef.current?.dispose(), []);

  // Start runs straight from the tap so iOS can show its motion prompt.
  const start = () => {
    const video = videoRef.current;
    if (!video) return;
    setErrors([]);
    setOffload(null);
    setServerTracking(null);
    setLive(null);
    setTrail(null);
    if (sessionRef.current && phase === 'failed') {
      // A failed start leaves a dead session; begin again from scratch.
      sessionRef.current.dispose();
      sessionRef.current = null;
    }
    void getSession(forceServer, lighting, liveSplats).start(video, name.trim() || undefined);
  };

  const finish = () => void sessionRef.current?.finish();

  const toggleLiveSplats = (on: boolean) => {
    setLiveSplats(on);
    saveLiveSplats(on);
  };

  const capturing = phase === 'capturing';
  const processing = phase === 'processing'; // server draining the video after Finish
  const serverMode = stats?.processing === 'server' || offload !== null;
  const shownTracking = serverMode ? asTrackingState(serverTracking?.state ?? '') : tracking;
  const shownSource = (serverMode ? serverTracking?.poseSource ?? '' : source) as PoseSource;
  const badge = trackingBadge(capturing || processing ? shownTracking : null, shownSource);
  const liveOn = serverMode && !!live && liveStreaming(live.phase);
  const shownStats = stats ? [...statsLine(stats), ...(serverMode && live && live.phase !== 'off' && live.phase !== '' ? [liveLine(live)] : [])] : [];
  const note = serverMode ? liveNote(live) : '';
  // The server-tracked camera path over the server preview while capturing.
  const path = serverMode && previewSource === 'server' && (capturing || processing) ? trail : null;
  const canFinish = !!stats && (serverMode ? stats.chunksRecorded >= 2 : stats.keyframes >= 2);
  const hint =
    phase === 'starting'
      ? 'Starting camera and tracker…'
      : processing
        ? 'Video uploaded — the server is tracking the rest before refining'
      : capturing && serverMode && !shownTracking
        ? 'Uploading video — the server is starting to track'
        : capturing && stats?.hold
        ? stats.hold
        : capturing && shownTracking === 'initializing'
          ? 'Point the camera at a marker to set the scale and origin'
          : capturing && shownTracking === 'lost'
            ? 'Tracking lost — point at a marker to relocalize'
            : '';

  return (
    <>
      <div ref={stageRef} className="capture-stage mb-3">
        <video ref={videoRef} playsInline muted />
        <canvas ref={overlayRef} className="capture-overlay" />
        <div className="capture-hud">
          <StatusBadge tone={badge.tone}>{badge.text}</StatusBadge>
          {stats?.imuFused && <StatusBadge tone="info">IMU fused</StatusBadge>}
          {serverMode && (
            <StatusBadge tone="info" uppercase={false} className="offload-chip" title={offload?.reason}>
              Server processing
            </StatusBadge>
          )}
          {phase !== 'idle' && <StatusBadge tone="neutral">{phase}</StatusBadge>}
          {device && (
            <StatusBadge tone={device.usable ? 'info' : 'neutral'} uppercase={false} className="lens-chip" title={device.fingerprint.label}>
              {chipText(device)}
            </StatusBadge>
          )}
          {device?.mismatch && (
            <StatusBadge tone="warning" uppercase={false} className="lens-chip">
              Lens profile mismatch — recalibrating
            </StatusBadge>
          )}
        </div>
        {(capturing || phase === 'finishing') && sessionRef.current && (
          <div className="capture-coverage">
            <CoverageRing coverage={sessionRef.current.coverage} version={stats?.keyframes ?? 0} />
          </div>
        )}
        {hint && <div className="capture-hint">{hint}</div>}
        {phase === 'idle' && (
          <div className="capture-hint">Lay the printed markers around the object, then press Start and walk slowly around it.</div>
        )}
        {preview && (
          <div className="capture-preview" data-source={previewSource} title={previewSource === 'server' ? (liveOn ? 'Live splats' : capturing ? 'Server tracking' : 'Server refinement') : 'Phone seed'}>
            {/* No auto-rotate while capturing: a spinning preview re-renders
                every animation frame on the main thread, which also grabs the
                camera frames, so on a slow GPU it starves the tracker (the
                browser e2e test measured 2 s/frame under SwiftShader). It
                redraws per new seed and can still be dragged. */}
            <SplatView cloud={preview} height="100%" autoRotate={!capturing && phase !== 'finishing'} fitKey={previewSource} path={path} />
          </div>
        )}
        {stats && (
          <div className="capture-stats">
            {shownStats.map((t, i) => (
              <span key={i}>{t}</span>
            ))}
          </div>
        )}
      </div>

      {note && <div className="small text-body-secondary mb-2 live-note">{note}</div>}

      {offload && (
        <Alert variant="info" className="py-2 offload-reason">
          <BsCloudArrowUp className="me-2" />
          {offload.reason}
        </Alert>
      )}

      {(computeRefused || (me && !hasCompute(me))) && (
        <Alert variant={computeRefused ? 'danger' : 'warning'} className="py-2 d-flex flex-wrap align-items-center gap-2" data-testid="compute-signin">
          <span className="flex-grow-1">
            {computeRefused
              ? 'The server refused to process this scan: this sign-in lacks compute access.'
              : 'This sign-in cannot process scans on the server (it lacks compute access).'}{' '}
            Sign in with compute{phase === 'idle' ? ' before you start' : ', then Finish again'}.
          </span>
          <Button size="sm" variant="outline-primary" as="a" href={computeLoginUrl(window.location.pathname + window.location.search)}>
            Sign in with compute
          </Button>
        </Alert>
      )}

      {errors.map((e, i) => (
        <Alert key={i} variant="danger" className="py-2">
          {e}
        </Alert>
      ))}

      <div className="d-flex flex-wrap align-items-center gap-2">
        {(phase === 'idle' || (phase === 'failed' && !scan)) && (
          <>
            <Text placeholder="Scan name (optional)" value={name} onChange={(e) => setName(e.target.value)} minWidth={20} aria-label="Scan name" />
            <Form.Check
              type="switch"
              id="process-on-server"
              label="Process on server"
              checked={forceServer}
              onChange={(e) => setForceServer(e.target.checked)}
              title="Record and upload the video; the server tracks it instead of this phone"
            />
            <Form.Check
              type="switch"
              id="live-splats"
              label="Live splats"
              checked={liveSplats}
              onChange={(e) => toggleLiveSplats(e.target.checked)}
              title="When the server processes the capture, train the splat model on its GPU while you sweep and show it live"
            />
            <Form.Check
              type="switch"
              id="flash-lighting"
              label="Flash lighting"
              checked={lighting}
              onChange={(e) => setLighting(e.target.checked)}
              title="Blink the torch during capture and lock exposure, so the server can separate surface colour from room lighting"
            />
            <Button variant="primary" onClick={start}>
              <BsCameraVideo className="me-2" />
              Start
            </Button>
          </>
        )}
        {capturing && (
          <Button variant="success" onClick={finish} disabled={!canFinish}>
            <BsCheck2 className="me-2" />
            Finish
          </Button>
        )}
        {phase === 'failed' && scan && sessionRef.current?.canRetryFinish && (
          <Button variant="success" onClick={finish}>
            <BsArrowClockwise className="me-2" />
            Retry finish
          </Button>
        )}
        {(processing || phase === 'refining' || phase === 'ready' || phase === 'failed') && scan && (
          <Link to={`/scans/${scan.id}`} className="btn btn-outline-primary">
            <BsEye className="me-2" />
            Open {scan.name}
          </Link>
        )}
        {capturing && stats && !canFinish && (
          <span className="small text-body-secondary">
            {serverMode ? 'Recording — keep sweeping around the object.' : 'Collect at least two keyframes before finishing.'}
          </span>
        )}
      </div>

      {(processing || phase === 'refining' || phase === 'ready') && (
        <div className="mt-3">
          <RefineProgress progress={progress} fallback={scan?.refine} />
        </div>
      )}
    </>
  );
}
